import {Injectable, signal} from '@angular/core';
import {ApiService} from './api.service';
import {User} from '../models/user.model';
import {RegisterProfileForm} from "../models/profile.model";
import {createLogger} from "../../shared/helpers/logger/logger";
import {Subject} from "rxjs";

const logger = createLogger('UserService');

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private currentUser = signal<User | null>(null);
    private userLoggedInSubject = new Subject<void>();
    userLoggedIn$ = this.userLoggedInSubject.asObservable();

    constructor(private apiService: ApiService) {
    }

    getCurrentUserSignal() {
        return this.currentUser.asReadonly();
    }

    isLoggedIn() {
        return !!this.currentUser();
    }

    async fetchCurrentUser(expand?: string[]): Promise<User> {
        const queryParams: any = {};
        if (expand && expand.length > 0) {
            queryParams.expand = expand.join(',');
        }
        const response = await this.apiService.get<User>('/users/me', queryParams);
        const user = response.body!;
        this.currentUser.set({...user});
        this.userLoggedInSubject.next();
        return user;
    }

    async updateUser(params: RegisterProfileForm): Promise<User> {
        const updatedUser = await this.apiService.put<User>(`/users/me`, {
            type: 'Profile',
            profile: params,
        });
        this.currentUser.set(updatedUser); // Update the signal with the new user data
        return updatedUser;
    }

    // Get the current logged-in user details
    async getCurrentUser(expand?: string[]): Promise<User> {
        const queryParams: any = {};

        // Construct query parameters based on input
        if (expand && expand.length > 0) {
            queryParams.expand = expand.join(',');
        }

        const response = await this.apiService.get<User>('/users/me', queryParams);
        return response.body!;
    }

    clearCurrentUser() {
        this.currentUser.set(null);
    }

    async initializeUser(): Promise<void> {
        const token = await this.apiService.getToken();
        if (token) {
            try {
                const user = await this.fetchCurrentUser(['profile']);
                this.currentUser.set(user);
                // currentUser is set within fetchCurrentUser
            } catch (error) {
                await this.apiService.setToken('', '', -9999999); // Clear invalid token
                this.currentUser.set(null);
            }
        } else {
            this.currentUser.set(null);
        }
    }
}
